import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, Mic, Square } from "lucide-react";
import { askClaude } from "../services/chat";
import { transcribeAudio } from "../services/openrouter";
import ChatMessage from "../components/ChatMessage";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

export default function Chat() {
  const [messages, setMessages] = useState<Msg[]>([
    {
      role: "assistant",
      content:
        "Hi! I can answer questions about your inventory across all months. Try asking things like:\n\n- *How much was spent on bags of calcium carbonate 2 months ago?*\n- *What's the total value of raw materials this month?*\n- *Show me all finished goods from last quarter*",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // auto-scroll
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setLoading(true);

    try {
      const reply = await askClaude(trimmed);
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `**Error:** ${e?.message || "Something went wrong"}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const reader = new FileReader();
        reader.onloadend = () => {
          const b64 = (reader.result as string).split(",")[1];
          transcribeAudio(b64, mime)
            .then((text) => sendMessage(text))
            .catch((err) => {
              setMessages((prev) => [
                ...prev,
                { role: "assistant", content: `**Transcription error:** ${err.message}` },
              ]);
            });
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start(250);
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4">
        <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-citilux-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Stock Chat</h1>
          <p className="text-sm text-gray-500 dark:text-metallic-silver/60">
            Ask questions about your inventory
          </p>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto space-y-4 pr-2 scroll-smooth"
      >
        {messages.map((m, i) => (
          <ChatMessage key={i} role={m.role} content={m.content} />
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-xl shrink-0 flex items-center justify-center bg-navy-700 text-metallic-silver">
              <MessageCircle className="w-4 h-4" />
            </div>
            <div className="bg-white dark:bg-navy-800/60 border border-light-border dark:border-navy-700/40 rounded-2xl px-4 py-3">
              <span className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-metallic-silver/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-metallic-silver/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-metallic-silver/60 animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="pt-4 border-t border-light-border dark:border-navy-700/40">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            placeholder="Type your question…"
            rows={1}
            className="flex-1 resize-none bg-white dark:bg-navy-800/60 border border-light-border dark:border-navy-700/40 rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-metallic-silver/50 focus:outline-none focus:ring-2 focus:ring-citilux-blue-500/50"
          />

          <button
            onMouseDown={recording ? undefined : startRecording}
            onMouseUp={recording ? stopRecording : undefined}
            onMouseLeave={recording ? stopRecording : undefined}
            className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              recording
                ? "bg-red-500 text-white shadow-lg shadow-red-500/30"
                : "bg-gray-100 dark:bg-navy-700 text-gray-500 dark:text-metallic-silver hover:bg-gray-200 dark:hover:bg-navy-600"
            }`}
          >
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>

          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className="h-10 w-10 rounded-xl bg-citilux-blue-600 hover:bg-citilux-blue-500 disabled:bg-gray-300 dark:disabled:bg-navy-700 text-white flex items-center justify-center shrink-0 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
